import {
  createAttrTxFromForm,
  createReusableRecordTxFromForm,
  deleteAttributeTransform,
  deleteRecordTransform,
  fetchDataSamples,
  loadAllDataSets,
  resetFormData,
  setContextualSupport,
  setFormError,
  setFormStageIndex,
  testAttributeTransform,
  testReusableRecordTransform,
  unsetContextualSupport,
  updateAttrTxFromForm,
  updateFormData,
  updateReusableRecordTxFromForm,
  createCodeContainer,
  updateCodeContainer,
  updateTransformDoc,
  getReusableTransformListing,
  updateSearchFilters,
  getAllReusableTransforms,
} from 'js/actions';
import {
  CREATE_ATTR_TRANSFORM_REQUEST,
  GET_DATA_SAMPLES_REQUEST,
  GET_DATA_SETS_REQUEST,
  GET_TRANSFORMED_SAMPLES_REQUEST,
  GET_CODE_CONTAINERS_REQUEST,
  GET_PUBLIC_CODE_CONTAINERS_REQUEST,
} from 'js/constants/actionTypes';
import { FORM_TYPE_CREATE_REUSABLE_TX } from 'js/constants/formTypes';
import { TRANSFORM_RULES_PROTO_STR } from 'js/constants/schemaEditor';
import { convertTransformToCode, decodeTranformCode } from 'js/lib/TransformsUtils';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import { TransformType } from 'new/components/ReusableTransform/types';
import { generateOptionsFromDataSets } from 'new/utils/DataSetUtil/generateOptionsFromDataSets';
import { connect } from 'react-redux';
import CreateReusableTxForm from './CreateReusableTxForm';
import {
  ATTR_TX_INPUT_SAMPLES,
  LANGUAGE_DEFAULTS,
  RECORD_LANGUAGE_DEFAULTS,
  RECORD_TX_INPUT_SAMPLES,
} from './form/TxFormConstants';
import { ReusableTransformUtil } from 'new/utils/ReusableTransform/argumentValues';
import { getReusableTransformListingEntities } from 'js/reducers/transforms';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';

const initializeFormData = (
  isEditMode = false,
  transformBeingEdited = null,
  preSelectedTransformType = TransformType.RECORD,
) => {
  let name = null,
    description = null,
    transformType = preSelectedTransformType,
    codeLanguage = 'python',
    code =
      transformType === TransformType.RECORD
        ? RECORD_LANGUAGE_DEFAULTS[codeLanguage]
        : LANGUAGE_DEFAULTS[codeLanguage];

  let txArgs = {
    v: 1,
    items: [
      {
        name: 'applyOnAttr',
        description: 'Apply on attribute',
      },
    ],
  };

  let samples =
    transformType === TransformType.RECORD ? RECORD_TX_INPUT_SAMPLES : ATTR_TX_INPUT_SAMPLES;
  let input = samples;
  let inputAttrs = ReusableTransformUtil.generatingInputAttributesFor(samples);

  let output = '';
  let txUpdateDone = false;
  let txUpdateId = null;
  let txUpdateError = null;

  if (isEditMode === true && !isNil(transformBeingEdited)) {
    name = transformBeingEdited.name;
    description = transformBeingEdited.description;
    transformType = transformBeingEdited.output_type;

    if (transformType === TransformType.RECORD) {
      //TODO: disallow arguments for now
      txArgs = {
        v: 1,
        items: [],
      };
      input = RECORD_TX_INPUT_SAMPLES;
      codeLanguage = transformBeingEdited.codeLanguage;

      if (has(transformBeingEdited, 'decoded')) {
        code = transformBeingEdited.decoded;
      } else {
        code = convertTransformToCode(transformBeingEdited.code);
      }
    } else {
      /**
       * TransformType.ATTRIBUTE
       */
      codeLanguage = transformBeingEdited.code_type;
      code = transformBeingEdited.code;
      if (has(transformBeingEdited, 'decoded')) {
        code = transformBeingEdited.decoded;
      } else if (!isNil(transformBeingEdited.code_encoding)) {
        code = decodeTranformCode(transformBeingEdited.code, transformBeingEdited.code_encoding);
      }

      txArgs = has(transformBeingEdited, 'custom_config.ui.txArgs')
        ? transformBeingEdited.custom_config.ui.txArgs
        : { v: 1, items: [] };
      input = ATTR_TX_INPUT_SAMPLES;
      inputAttrs = ReusableTransformUtil.generatingInputAttributesFor(ATTR_TX_INPUT_SAMPLES);
    }
  }

  let txArgsTestData = map(txArgs.items, (arg) => ({
    fName: arg.name,
    ...TRANSFORM_RULES_PROTO_STR,
    value: '',
  }));

  return {
    name,
    description,
    transformType,
    codeLanguage,
    code,
    txArgs,
    input,
    inputAttrs,
    output,
    shouldInitFormData: true,
    testAttrTransformMessage: null,
    txUpdateDone,
    txUpdateId,
    txUpdateError,
    txArgsTestData,
  };
};

const mapStateToProps = (state, ownProps) => {
  const {
    entities: { dataSets },
    forms,
    request,
    dataSets: { byId, lastUpdated },
    samples,
  } = state;

  const transformsData = getReusableTransformListingEntities(state);
  let form = forms[FORM_TYPE_CREATE_REUSABLE_TX];
  let currentStage = form ? form.currentStage : 0;
  let formData = form ? form.data : null;
  let isFormAwaitingValidation = form ? form.awaitingValidation : false;

  if (isEmpty(formData) || formData?.shouldInitFormData === true) {
    formData = initializeFormData(
      ownProps.isEditMode,
      ownProps.transformBeingEdited,
      ownProps?.preSelectedTransformType,
    );
    if (!ownProps.isEditMode && ownProps.preSelectedTransformType) {
      formData.transformType = ownProps.preSelectedTransformType;
    }
  }

  let areDataSetsLoaded = !!lastUpdated;
  let dataSetOptions = generateOptionsFromDataSets(byId, dataSets);

  let isFetchingOutputSamples =
    !!request[GET_TRANSFORMED_SAMPLES_REQUEST] || !!request[CREATE_ATTR_TRANSFORM_REQUEST];
  let isFetchingDataSets = !!request[GET_DATA_SETS_REQUEST];
  let isFetchingDataSetSamples = !!request[GET_DATA_SAMPLES_REQUEST];
  const isFetchingReusableTransforms = !!(
    request[GET_CODE_CONTAINERS_REQUEST] || request[GET_PUBLIC_CODE_CONTAINERS_REQUEST]
  );

  const selectedSampleDataSet = formData?.selectedDataSetId && dataSets[formData.selectedDataSetId];

  return {
    isCapsuleCluster: isCapsuleCluster(selectedSampleDataSet),
    transformsData,
    currentStage,
    isFormAwaitingValidation,
    formData,
    samples,
    dataSetOptions,
    areDataSetsLoaded,
    isFetchingOutputSamples,
    isFetchingDataSets,
    isFetchingDataSetSamples,
    isFetchingReusableTransforms,
  };
};

export default connect(mapStateToProps, {
  updateFormData,
  resetFormData,
  setFormError,
  setFormStageIndex,
  deleteAttributeTransform,
  createAttrTxFromForm,
  updateAttrTxFromForm,
  testAttributeTransform,
  testReusableRecordTransform,
  deleteRecordTransform,
  updateTransformDoc,
  createReusableRecordTxFromForm,
  updateReusableRecordTxFromForm,
  createCodeContainer,
  updateCodeContainer,
  loadAllDataSets,
  fetchDataSamples,
  setContextualSupport,
  unsetContextualSupport,
  getReusableTransformListing,
  getAllReusableTransforms,
  updateSearchFilters,
})(CreateReusableTxForm);
